
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { match as Match } from 'react-router-dom';
import { Location } from 'history';
import { AxiosError } from 'axios';
import ReactTable from 'react-table';
import moment from 'moment';
import history from '../../../history';

import PayrollTable from './PayrollTable';
import PayrollFilters from './PayrollFilters';

import { fetchPayroll } from '../../../actions/Payroll';
import { executeHttpRequest } from '../../../epics/api';
import {
    withPushFiltersToUrl,
    withRefreshTableOnFilterChange,
} from '../../operations/common/decorators';
import { PayrollState } from '../../../reducers/payrollReducer';
import { TPageNotification, TUser } from '../../../types';
import {
    TGenerateTimesheetPayload,
    TSelectedBookingSummary,
    TPayrollFreelancer,
    TExtraRateOption,
} from './types';
import { convertDurationToTimeFormat } from './utils';
import { TRootState } from '../../../reducers';

interface PayrollProps extends PayrollState {
    location: Location;
    match: Match;
    notification: TPageNotification;
    user: TUser;
    load: (searchParams: URLSearchParams) => void;

    // Injected by withPushFiltersToUrl
    setTable: (table: ReactTable | null) => void;
    onFilterChange: (id: string, value: string) => void;
    onSortedChange: (newSorted: { id: string; desc: boolean }[]) => void;
    onPageChange: (pageIndex: number) => void;
    currentPage: number;
}

interface PayrollComponentState {
    bookings_after: string;
    bookings_before: string;
    electronic_authorisation: string;
    timesheet_generated: string;
    lastFilterSignature: string;
    isGenerateEnabled: boolean;
    expandedRows: number[];
    // We rely on this map inside setState updaters; lint cannot detect those usages.
    // eslint-disable-next-line react/no-unused-state
    selectedBookings: Record<number, TSelectedBookingSummary[]>;
    selectedBookingList: TSelectedBookingSummary[];
    pendingDeselection: Record<number, number[]>;
    generatedBookings: Record<number, number[]>;
    extraRateOptions: TExtraRateOption[];
}

class Payroll extends Component<PayrollProps, PayrollComponentState> {
    // Keep a ref per expanded freelancer so we can trigger targeted reloads.
    // These are refs to PayrollSubComponent instances which have reloadSubData and updateBookingsWithTimesheet methods
    // Note: Using 'any' here because these are PayrollSubComponent instances with custom methods, not ReactTable instances
    private subComponentRefs: Record<number, any> = {};

    /** Stable fingerprint of payroll list query params; when it changes, expanded rows collapse. */
    private static buildFilterSignature = (search: string): string => {
        const params = new URLSearchParams(search);
        const keys = [...new Set(params.keys())]
            .filter(k => k !== 'ordering')
            .sort();
        return keys
            .flatMap(key =>
                params
                    .getAll(key)
                    .sort()
                    .map(value => `${key}=${value}`),
            )
            .join('&');
    };

    static getDerivedStateFromProps(
        nextProps: PayrollProps,
        prevState: PayrollComponentState,
    ) {
        const nextSignature = Payroll.buildFilterSignature(
            nextProps.location.search,
        );
        if (nextSignature !== prevState.lastFilterSignature) {
            return {
                expandedRows: [] as number[],
                lastFilterSignature: nextSignature,
            };
        }
        return null;
    }

    constructor(props: PayrollProps) {
        super(props);
        this.state = {
            bookings_after: moment()
                .startOf('isoWeek')
                .startOf('day')
                .subtract(1, 'week')
                .toISOString(),
            bookings_before: moment()
                .endOf('isoWeek')
                .endOf('day')
                .subtract(1, 'week')
                .toISOString(),
            electronic_authorisation: '',
            timesheet_generated: '',
            lastFilterSignature: Payroll.buildFilterSignature(
                history.location.search,
            ),
            isGenerateEnabled: false,
            expandedRows: [] as number[],
            // eslint-disable-next-line react/no-unused-state
            selectedBookings: {},
            selectedBookingList: [],
            pendingDeselection: {},
            generatedBookings: {},
            extraRateOptions: [],
        };
    }

    componentDidMount() {
        const { search } = history.location;
        const currentUrlParams = new URLSearchParams(search);
        if (search) {
            if (currentUrlParams.has('bookings_client')) {
                currentUrlParams.delete('bookings_client');
                history.push({
                    pathname: '/payments/payroll/',
                    search: currentUrlParams.toString(),
                });
                return;
            }
            this.props.load(currentUrlParams);
        } else {
            // Seed the URL with default date filters so refreshes/bookmarks are stable.
            const paramString = new URLSearchParams(
                this.getDefaultQueryParams() as Record<string, string>,
            ).toString();
            history.push({
                pathname: '/payments/payroll/',
                search: paramString,
            });
        }
        this.fetchExtraRateOptions();
    }

    fetchExtraRateOptions = async() => {
        try {
            const response = await executeHttpRequest('rates/?is_active=true', 'GET');
            const content = response?.data?.content;
            const mapped: TExtraRateOption[] = content.map((option: { id: number; code?: string; name?: string }) => ({
                id: option.id,
                code: option.code || (option.id != null ? String(option.id) : ''),
                name: option.name || option.code || '',
                description: option.name || '',
            })).filter((option: TExtraRateOption) => option.code);
            this.setState({ extraRateOptions: mapped });
        } catch (error) {
            this.props.notification.error('Error', 'Failed to fetch extra rates');
            this.setState({ extraRateOptions: [] });
        }
    };

    getDefaultQueryParams = () => {
        const { bookings_after, bookings_before } = this.state;
        return {
            bookings_after,
            bookings_before,
            electronic_authorisation: this.state.electronic_authorisation,
            timesheet_generated: this.state.timesheet_generated,
        };
    };

    onRowExpand = (id: number) => {
        if (!this.state.expandedRows.includes(id)) {
            this.setState(prevState => ({
                expandedRows: [...prevState.expandedRows, id],
            }));
        }
    };

    onRowCollapse = (id: number) => {
        this.setState(
            prevState => ({
                expandedRows: prevState.expandedRows.filter(
                    rowId => rowId !== id,
                ),
            }),
            () => {
                this.handleSelectionChange(id, []);
            },
        );
    };

    onFilterChange = (id: string, value: string) => {
        this.props.onFilterChange(id, value);
    };

    setRef = (id: number, ref: any) => {
        if (ref) {
            this.subComponentRefs[id] = ref;
        } else {
            delete this.subComponentRefs[id];
        }
    };

    getExpandedIndices = () => {
        const { content } = this.props;
        const { expandedRows } = this.state;
        const expanded: { [key: number]: boolean } = {};

        if (content && expandedRows.length > 0) {
            content.forEach((row: TPayrollFreelancer, index: number) => {
                if (expandedRows.includes(row.id)) {
                    expanded[index] = true;
                }
            });
        }
        return expanded;
    };

    refreshTable = () => {
        this.props.load(new URLSearchParams(history.location.search));
        // Each expanded row renders its own sub-table; refresh all of them.
        Object.values(this.subComponentRefs).forEach(subComponent => {
            if (subComponent?.reloadSubData) {
                subComponent.reloadSubData();
            }
        });
    };

    handleSelectionChange = (
        freelancerId: number,
        bookings: TSelectedBookingSummary[],
    ) => {
        this.setState(prevState => {
            const selectedBookings = { ...prevState.selectedBookings };
            if (bookings.length) {
                selectedBookings[freelancerId] = bookings;
            } else {
                delete selectedBookings[freelancerId];
            }
            const selectedBookingList = Object.values(selectedBookings).flat();
            return {
                selectedBookings,
                selectedBookingList,
                isGenerateEnabled: selectedBookingList.length > 0,
            };
        });
    };

    handleGenerateBookings = async(payload: TGenerateTimesheetPayload): Promise<void> => {
        const { freelancer, bookings: bookingIds, pay_rate } = payload;

        // Transform payload to match API format
        const apiPayload = {
            job_description: payload.description,
            personnel_reference: payload.personnel_reference,
            client_reference: payload.client_reference,
            consultant_code: payload.consultant_code,
            employer_reference: payload.employer_reference,
            division_reference: payload.division,
            department_reference: payload.department,
            timesheet_date: payload.timesheet_date,
            freelancer: payload.freelancer,
            agent: payload.agent,
            client: payload.client,
            bookings: payload.bookings,
            pay_rates: pay_rate.map(rate => ({
                rate_code: rate.rate_code,
                description: rate.description,
                duration: convertDurationToTimeFormat(rate.duration),
                freelancer_pay_per_hour: rate.hourly_rate.toFixed(2),
                client_pay_per_hour: rate.client_pay.toFixed(2),
                adhoc_pay_rate: rate.adhoc_pay_rate || false,
            })),
        };

        try {
            const response = await executeHttpRequest(
                'timesheets/',
                'POST',
                apiPayload,
            );

            if (response.status === 200 || response.status === 201) {
                // Extract timesheet data from response
                const timesheetId = response.data?.content?.id;
                const timesheetNumber = response.data?.content?.timesheet_number;
                const timesheetStatus = response.data?.content?.status_display;
                const timesheetStatusCode = response.data?.content?.status;

                // Success - deselect the bookings and mark as generated
                this.setState(prevState => {
                    const selectedBookings = { ...prevState.selectedBookings };
                    const pendingDeselection = { ...prevState.pendingDeselection };
                    const generatedBookings = { ...prevState.generatedBookings };

                    if (selectedBookings[freelancer]) {
                        selectedBookings[freelancer] = selectedBookings[freelancer].filter(
                            storedBooking => !bookingIds.includes(storedBooking.booking.id),
                        );
                        if (!selectedBookings[freelancer].length) {
                            delete selectedBookings[freelancer];
                        }
                    }

                    if (!pendingDeselection[freelancer]) {
                        pendingDeselection[freelancer] = [];
                    }
                    bookingIds.forEach(bookingId => {
                        if (!pendingDeselection[freelancer].includes(bookingId)) {
                            pendingDeselection[freelancer].push(bookingId);
                        }
                    });

                    // Track generated bookings to disable them
                    if (!generatedBookings[freelancer]) {
                        generatedBookings[freelancer] = [];
                    }
                    bookingIds.forEach(bookingId => {
                        if (!generatedBookings[freelancer].includes(bookingId)) {
                            generatedBookings[freelancer].push(bookingId);
                        }
                    });

                    const selectedBookingList = Object.values(selectedBookings).flat();

                    return {
                        selectedBookings,
                        selectedBookingList,
                        isGenerateEnabled: selectedBookingList.length > 0,
                        pendingDeselection,
                        generatedBookings,
                    };
                });

                // Update the sub-component with timesheet data
                const subComponentRef = this.subComponentRefs[freelancer];
                if (subComponentRef && timesheetId) {
                    subComponentRef.updateBookingsWithTimesheet(
                        bookingIds,
                        timesheetId,
                        timesheetNumber,
                        timesheetStatus,
                        timesheetStatusCode,
                    );
                }

                // Show success notification
                this.props.notification.success(
                    'Success',
                    'Timesheet generated successfully',
                );
            }
        } catch (error) {
            // Error - parse API error structure
            const axiosError = error as AxiosError<{
                meta?: { detail?: Record<string, string[]> };
                message?: string;
                error?: string;
            }>;
            let errorMessage = 'Failed to generate timesheet. Please try again.';

            if (axiosError.response?.data?.meta?.detail) {
                const { detail } = axiosError.response.data.meta;
                // detail is an object with field names as keys and arrays of error messages as values
                // e.g., { "bookings": ["Error message 1", "Error message 2"], "timesheet_date": ["Date error"] }
                const errorMessages: string[] = [];

                Object.entries(detail).forEach(([field, messages]) => {
                    if (Array.isArray(messages)) {
                        messages.forEach(msg => {
                            errorMessages.push(`${field}: ${msg}`);
                        });
                    }
                });

                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join('\n');
                }
            } else if (axiosError.response?.data?.message) {
                errorMessage = axiosError.response.data.message;
            } else if (axiosError.response?.data?.error) {
                errorMessage = axiosError.response.data.error;
            }

            this.props.notification.error(
                'Error',
                errorMessage,
            );

            // Re-throw error so modal knows to keep the group open
            throw error;
        }
    };

    handleDeselectProcessed = (
        freelancerId: number,
        bookingIds: number[],
    ) => {
        this.setState(prevState => {
            const pendingDeselection = { ...prevState.pendingDeselection };
            if (!pendingDeselection[freelancerId]) {
                return null;
            }
            pendingDeselection[freelancerId] = pendingDeselection[
                freelancerId
            ].filter(id => !bookingIds.includes(id));
            if (!pendingDeselection[freelancerId].length) {
                delete pendingDeselection[freelancerId];
            }
            return { pendingDeselection };
        });
    };

    render() {
        return (
            <div className="table-container">
                <PayrollFilters
                    onFilterChange={this.onFilterChange}
                    history={history}
                    reloadData={this.refreshTable}
                    notification={this.props.notification}
                    isGenerateEnabled={this.state.isGenerateEnabled}
                    selectedBookings={this.state.selectedBookingList}
                    onGenerateTimesheet={this.handleGenerateBookings}
                    rateOptions={this.state.extraRateOptions}
                />
                <PayrollTable
                    expanded={this.getExpandedIndices()}
                    match={this.props.match}
                    onRowExpand={this.onRowExpand}
                    onRowCollapse={this.onRowCollapse}
                    loading={this.props.fetching}
                    content={this.props.content}
                    meta={this.props.meta}
                    notification={this.props.notification}
                    reloadData={this.refreshTable}
                    onSortedChange={this.props.onSortedChange}
                    onPageChange={this.props.onPageChange}
                    page={this.props.currentPage}
                    errorMsg={this.props.error}
                    history={history}
                    setRef={this.setRef}
                    setTable={this.props.setTable}
                    onSelectionChange={this.handleSelectionChange}
                    pendingDeselection={this.state.pendingDeselection}
                    onDeselectionProcessed={this.handleDeselectProcessed}
                    generatedBookings={this.state.generatedBookings}
                    rateOptions={this.state.extraRateOptions}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: TRootState) => ({
    ...state.payroll,
    user: state.user,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    load: (searchParams: URLSearchParams) => dispatch(fetchPayroll(searchParams) as any),
});

// Compose the HOCs manually since decorators can be tricky in migration without config changes
const DecoratedPayroll = withPushFiltersToUrl(
    withRefreshTableOnFilterChange(Payroll),
);

export default connect(mapStateToProps, mapDispatchToProps)(DecoratedPayroll);

